<template>
    <div
        class="q-pb-xl"
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <div class="text-h4 text-weight-medium q-mb-lg">
            {{ $t(`Change Password`) }}
        </div>
        <q-form
            @submit="handleSubmit"
            ref="resetPasswordForm"
            class="q-px-xs"
            :class="{ 'ios-form': $q.platform.is.ipad }"
        >
            <q-input
                for="current-field"
                v-model="passwordForm.currentPassword"
                :type="showCurrentPassword ? 'input' : 'password'"
                :label="$t('Current Password')"
                autocomplete="off"
                class="q-mb-md bg-white"
                outlined
            >
                <template v-slot:append>
                    <q-icon
                        v-show="passwordForm.currentPassword.length > 0"
                        @click="showCurrentPassword = !showCurrentPassword"
                        :name="
                            showCurrentPassword
                                ? 'fas fa-eye-slash'
                                : 'fas fa-eye'
                        "
                        class="cursor-pointer"
                    />
                </template>
            </q-input>
            <q-space class="q-py-xs" />

            <password-and-confirmation
                @inputUpdate="updatePassword"
                :currentPassword="passwordForm.currentPassword"
            />

            <div class="float-right q-pt-none">
                <q-btn
                    @click="navigateToProfile"
                    :disable="isLoading"
                    class="q-mr-md"
                    color="white"
                    text-color="primary"
                    outline
                    >{{ $t('Cancel') }}</q-btn
                >
                <q-btn
                    id="submit-button"
                    :label="$t('Save')"
                    type="submit"
                    :disabled="disableSubmit"
                    class="q-my-md"
                    color="primary"
                />
            </div>
        </q-form>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import PasswordAndConfirmation from '@components/PasswordAndConfirmation.vue';
import { mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';

export default {
    name: 'UserProfileResetPassword',
    components: { PasswordAndConfirmation },
    mixins: [BreadcrumbsManager],
    mounted() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Home'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.$t('Profile'),
                route: {
                    name: 'profile'
                }
            },
            {
                label: this.$t('Change Password')
            }
        ]);
    },
    data() {
        return {
            passwordForm: {
                currentPassword: '',
                newPassword: '',
                confirmedPassword: ''
            },
            showCurrentPassword: false,
            disableSubmit: true
        };
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        /**
         * Update password form.
         * @param {Object} passwordObj - Password and confirmation object
         */
        updatePassword(passwordObj) {
            this.passwordForm.newPassword = passwordObj.new;
            this.passwordForm.confirmPassword = passwordObj.confirmation;
            this.disableSubmit = !passwordObj.isConfirmed;
        },
        /**
         * Handle form submission.
         */
        async handleSubmit() {
            // begin loading
            this.beginLoading(`${this.$t('Sending request')}...`);

            const payload = {
                current_password: this.passwordForm.currentPassword,
                new_password: this.passwordForm.newPassword
            };

            try {
                const response = await this.$axios.post(
                    'users/set_password/',
                    payload
                );
                // if status code is 204, the password is successfully updated
                if (response.status === 204) {
                    this.$notifySuccess(
                        this.$t('Your password was successfully updated')
                    );

                    // reset the password fields
                    this.passwordForm.currentPassword = '';
                    this.passwordForm.newPassword = '';
                    this.passwordForm.confirmPassword = '';

                    this.navigateToProfile();
                }
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                // display the password issues on the banner)
                if (error['new_password']) {
                    // Clear the error messages array
                    this.errorMessages = [];
                    error['new_password'].forEach(errorMsg => {
                        // Push the error message to the array
                        this.errorMessages.push(errorMsg);
                    });
                    this.$notifyError(
                        this.$t(
                            'Please review the errors below and try to set your password again.'
                        ),
                        error
                    );
                } else if (error['uid']) {
                    // notify uid issues
                    error['uid'].forEach(errorMsg => {
                        this.$notifyError(errorMsg, error);
                    });
                } else if (error['token']) {
                    // notify token issues
                    error['token'].forEach(errorMsg => {
                        this.$notifyError(errorMsg, error);
                    });
                } else if (error['current_password']) {
                    // Modify the invalid password error message to be more clear
                    if (
                        error['current_password'].includes('Invalid password.')
                    ) {
                        this.$notifyError(
                            this.$t(
                                'The current password is incorrect. Please try again.'
                            ),
                            error
                        );
                    } else {
                        // notify current password issues
                        error['current_password'].forEach(errorMsg => {
                            this.$notifyError(errorMsg, error);
                        });
                    }
                } else if (error.code && error.code === 'password_no_reuse') {
                    // notify password reuse issues
                    this.$notifyError(
                        this.$t(
                            "Your new password can't be the same as your current one. Please choose a different password."
                        )
                    );
                } else {
                    this.$notifyError(
                        this.$t(
                            'Unable to reset your password. Please try again later.'
                        ),
                        error
                    );
                }
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Handle cancel.
         */
        navigateToProfile() {
            this.$router.push({
                name: 'profile'
            });
        }
    }
};
</script>
